/**
 * Where a user belongs, given auth + onboarding state — the pure half of
 * `use-auth-routing.ts`, extracted so it can be executed in tests. The hook it
 * came from imports expo-router and two zustand stores and cannot load outside
 * a device runtime; this module has no runtime imports at all, which is why
 * `needsProfile` arrives as a plain boolean rather than a user object.
 */
import type { Href } from "expo-router";

import type { AuthStatus } from "@/state/auth-store";

export function redirectTarget({
  group,
  step,
  status,
  needsProfile,
  permissionsDone,
  mobileWeb = false,
  pendingHref = null,
}: {
  group: string | undefined;
  /** The screen within the group, e.g. "permissions" in "(onboarding)/permissions". */
  step: string | undefined;
  status: AuthStatus;
  needsProfile: boolean;
  permissionsDone: boolean;
  /**
   * Web platform in a phone's browser (see `lib/mobile-web.ts`). Optional so
   * native callers and existing tests state nothing about a fact that cannot
   * apply to them.
   */
  mobileWeb?: boolean;
  /**
   * A deep link the guard bounced this visitor off before they signed in
   * (`lib/pending-destination.ts`). Once they are authenticated and fully
   * onboarded, leaving the auth/onboarding groups lands THERE instead of
   * home — the invitation they arrived with, not a blank start screen.
   */
  pendingHref?: string | null;
}): Href | null {
  if (status === "loading") return null;

  // The web business portal and public spot pages manage their own access
  // (the spot page is fully public, opened from social links), so the auth
  // guard must not redirect away from them.
  if (group === "business" || group === "spot" || group === "legal") return null;

  // OAuth callbacks are transient handoff screens. Let the auth request finish
  // and allow the callback route to choose the next destination so users do
  // not see the login screen flash after a successful provider redirect.
  if (group === "oauthredirect") return null;

  // An invite envelope opened in a phone's browser renders the get-the-app
  // interstitial (`app/invite/[code].tsx`), signed in or not — a login wall
  // here discards the invitation and strands its recipient in a web app that
  // was never built for that screen. Only the deep-linked envelope (`step`
  // present) is exempt: `/invite` itself is the signed-in share screen and
  // keeps the guard.
  if (mobileWeb && group === "invite" && step !== undefined) return null;

  const inAuth = group === "(auth)";
  const inOnboarding = group === "(onboarding)";
  // The entry route ("/", index.tsx) has no group segment. It only renders a
  // splash and must always redirect to the correct destination.
  const atRoot = group === undefined;

  if (status === "unauthenticated") {
    return inAuth ? null : "/(auth)/welcome";
  }

  // Session valid, identity unknown. Deliberately BEFORE the profile check:
  // `needsProfileSetup(null)` is true, so without this the next branch would
  // send a fully signed-up person to onboarding — which is the bug this state
  // was added to end, not a case to fall through into.
  if (status === "unavailable") {
    return inAuth && step === "unavailable" ? null : "/(auth)/unavailable";
  }

  // Authenticated from here. Onboarding runs in order and permissions is last,
  // so the profile step is checked first and a user part-way through it is put
  // back on it — being inside the group is not enough. Asking for permissions
  // in the middle of onboarding is what a social sign-in used to do, and the
  // system dialogs it raises have no relationship to the screen behind them.
  if (needsProfile) {
    return inOnboarding && step === "name" ? null : "/(onboarding)/name";
  }

  // Location is offered here, never demanded. Once this step is marked done, the
  // app opens and each feature that truly needs a fix refuses at its own gate.
  if (!permissionsDone) {
    return inOnboarding && step === "permissions"
      ? null
      : "/(onboarding)/permissions";
  }

  // Fully signed in and onboarded. Someone still inside auth/onboarding (or
  // on the splash) is leaving it — and if they arrived carrying an invitation
  // the guard bounced them off, that is where they were going.
  if (inAuth || inOnboarding || atRoot) {
    return (pendingHref as Href | null) ?? "/(tabs)/home";
  }
  return null;
}

/**
 * How long to wait before re-issuing a redirect that has not taken effect.
 *
 * Doubling, capped. The common case is a NavigationContainer that attaches a
 * frame or two late, which the first two attempts cover at the old cadence;
 * beyond that the app is somewhere it should not be and the cheapest correct
 * thing is to keep asking, slowly, rather than to stop. At the cap this is one
 * attempt every 2s -- a quarter of the flat 120ms rate it replaces -- and it
 * costs nothing but a dropped navigation intent: no mount, no fetch, no query.
 */
export const REDIRECT_RETRY_MS = 120;
export const REDIRECT_RETRY_MAX_MS = 2000;

export function nextRedirectDelay(previous: number): number {
  return Math.min(previous * 2, REDIRECT_RETRY_MAX_MS);
}
